import React, { useState } from "react";
import encountersService, {
  SOAPNote,
} from "../../../services/encountersService";

interface SOAPEditModalProps {
  encounter: SOAPNote;
  onClose: () => void;
  onSaved: (updated: SOAPNote) => void;
}

type SoapKey = "subjective" | "objective" | "assessment" | "plan";

const SOAP_SECTION_LABELS: Record<SoapKey, { label: string; color: string }> = {
  subjective: { label: "Subjective", color: "blue" },
  objective: { label: "Objective", color: "green" },
  assessment: { label: "Assessment", color: "yellow" },
  plan: { label: "Plan", color: "purple" },
};

// Purge-safe full class strings for the active tab (per color)
const TAB_ACTIVE_CLASSES: Record<string, string> = {
  blue: "border-blue-500 text-blue-600",
  green: "border-green-500 text-green-600",
  yellow: "border-yellow-500 text-yellow-600",
  purple: "border-purple-500 text-purple-600",
  pink: "border-pink-500 text-pink-600",
  teal: "border-teal-500 text-teal-600",
};
const PALETTE_COLORS = ["blue", "green", "yellow", "purple", "pink", "teal"];

const parseInitialValue = (value: any): any => {
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (
      (trimmed.startsWith("{") && trimmed.endsWith("}")) ||
      (trimmed.startsWith("[") && trimmed.endsWith("]"))
    ) {
      try {
        return JSON.parse(trimmed);
      } catch {
        return value;
      }
    }
  }
  return value ?? {};
};

const formatLabel = (key: string): string => {
  return key
    .replace(/_/g, " ")
    .replace(/([A-Z])/g, " $1")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const SOAPEditModal: React.FC<SOAPEditModalProps> = ({
  encounter,
  onClose,
  onSaved,
}) => {
  // Custom-template notes edit generated_document; SOAP notes edit the 4 columns.
  const isTemplate = !!encounter.generated_document;
  const soapKeys: SoapKey[] = ["subjective", "objective", "assessment", "plan"];
  const tabKeys: string[] = isTemplate
    ? Object.keys(encounter.generated_document || {})
    : soapKeys;

  const [sections, setSections] = useState<Record<string, any>>(() => {
    if (isTemplate) {
      // Copy generated_document as-is (values are string | string[])
      return { ...(encounter.generated_document || {}) };
    }
    return {
      subjective: parseInitialValue(encounter.subjective),
      objective: parseInitialValue(encounter.objective),
      assessment: parseInitialValue(encounter.assessment),
      plan: parseInitialValue(encounter.plan),
    };
  });
  const [activeTab, setActiveTab] = useState<string>(tabKeys[0]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const tabMeta = (key: string, index: number) => {
    if (!isTemplate && key in SOAP_SECTION_LABELS) {
      return SOAP_SECTION_LABELS[key as SoapKey];
    }
    return {
      label: formatLabel(key),
      color: PALETTE_COLORS[index % PALETTE_COLORS.length],
    };
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      if (isTemplate) {
        const updated = await encountersService.updateEncounter(encounter.id, {
          generated_document: sections,
        });
        onSaved(updated);
        return;
      }

      const prepareField = (val: any) => {
        if (typeof val === "string") {
          const trimmed = val.trim();
          if (
            (trimmed.startsWith("{") && trimmed.endsWith("}")) ||
            (trimmed.startsWith("[") && trimmed.endsWith("]"))
          ) {
            try {
              return JSON.parse(trimmed);
            } catch {
              // ignore
            }
          }
        }
        return val;
      };

      const updated = await encountersService.updateEncounter(encounter.id, {
        subjective: prepareField(sections.subjective),
        objective: prepareField(sections.objective),
        assessment: prepareField(sections.assessment),
        plan: prepareField(sections.plan),
      });
      onSaved(updated);
    } catch (err: any) {
      setError(err?.response?.data?.detail || err.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  // --- Template-mode handlers (operate directly on sections[activeTab]) ---
  const handleTemplateString = (value: string) =>
    setSections((prev) => ({ ...prev, [activeTab]: value }));

  const handleTemplateArray = (index: number, value: string | null) =>
    setSections((prev) => {
      const arr = Array.isArray(prev[activeTab]) ? [...prev[activeTab]] : [];
      if (value === null) arr.splice(index, 1);
      else if (index === -1) arr.push(value);
      else arr[index] = value;
      return { ...prev, [activeTab]: arr };
    });

  const handleFieldChange = (path: string[], value: any) => {
    setSections((prev) => {
      const newSections = { ...prev };
      let current = newSections[activeTab];

      // Navigate to the parent of the target field
      for (let i = 0; i < path.length - 1; i++) {
        const key = path[i];
        if (!current[key]) current[key] = {};
        current[key] = { ...current[key] };
        current = current[key];
      }

      // Update the target field
      current[path[path.length - 1]] = value;
      return newSections;
    });
  };

  const handleArrayChange = (
    path: string[],
    index: number,
    value: string | null,
  ) => {
    setSections((prev) => {
      const newSections = { ...prev };
      let current = newSections[activeTab];

      for (let i = 0; i < path.length - 1; i++) {
        const key = path[i];
        if (!current[key]) current[key] = {};
        current[key] = { ...current[key] };
        current = current[key];
      }

      const lastKey = path[path.length - 1];
      const arr = Array.isArray(current[lastKey]) ? [...current[lastKey]] : [];

      if (value === null) {
        // Remove
        arr.splice(index, 1);
      } else if (index === -1) {
        // Add
        arr.push(value);
      } else {
        // Update
        arr[index] = value;
      }

      current[lastKey] = arr;
      return newSections;
    });
  };

  // Reusable array-item editor UI (used by SOAP nested arrays and template arrays)
  const renderArrayItems = (
    items: string[],
    label: string,
    onChange: (index: number, value: string | null) => void,
  ) => (
    <div className="flex flex-col gap-3 p-4 bg-gray-50/50 rounded-xl border border-gray-100 shadow-sm">
      <div className="flex items-center justify-between border-b border-gray-100 pb-2 mb-1">
        <label className="text-sm font-bold text-gray-800">{label}</label>
        <button
          onClick={() => onChange(-1, "")}
          className="px-2.5 py-1.5 text-xs font-semibold text-blue-600 bg-white border border-blue-200
            rounded-lg hover:bg-blue-50 hover:border-blue-300 transition-all flex items-center gap-1.5 shadow-sm"
        >
          <svg
            className="w-3.5 h-3.5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M12 4v16m8-8H4"
            />
          </svg>
          Add Item
        </button>
      </div>
      <div className="flex flex-col gap-2.5">
        {items.length === 0 ? (
          <p className="text-xs text-gray-400 italic py-2 px-1">
            No items currently listed.
          </p>
        ) : (
          items.map((item, idx) => (
            <div key={idx} className="flex gap-3 items-start group">
              <div className="flex-1">
                <textarea
                  className="w-full text-sm text-gray-900 border border-gray-200 rounded-lg px-3 py-2
                    focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 shadow-sm transition-all min-h-[38px] h-[38px] leading-relaxed"
                  value={item}
                  onChange={(e) => onChange(idx, e.target.value)}
                  placeholder={`Enter ${label.toLowerCase()} item...`}
                />
              </div>
              <button
                onClick={() => onChange(idx, null)}
                className="mt-1.5 p-1.5 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all opacity-0 group-hover:opacity-100 focus:opacity-100"
                title="Remove item"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                  />
                </svg>
              </button>
            </div>
          ))
        )}
      </div>
    </div>
  );

  const renderField = (
    key: string,
    value: any,
    path: string[],
    depth: number = 0,
  ) => {
    const fullPath = [...path, key];
    const label = formatLabel(key);

    if (Array.isArray(value)) {
      return (
        <div key={key}>
          {renderArrayItems(value, label, (index, val) =>
            handleArrayChange(fullPath, index, val),
          )}
        </div>
      );
    }

    if (typeof value === "object" && value !== null) {
      return (
        <div
          key={key}
          className={`flex flex-col gap-5 p-5 ${depth === 0 ? "bg-white" : "bg-gray-50/30"} rounded-2xl border border-gray-100 shadow-sm`}
        >
          <label className="text-sm font-black text-gray-900 flex items-center gap-2">
            <span className={`w-1 h-4 rounded-full bg-blue-500`}></span>
            {label}
          </label>
          <div className="flex flex-col gap-5">
            {Object.entries(value).map(([subKey, subValue]) =>
              renderField(subKey, subValue, fullPath, depth + 1),
            )}
          </div>
        </div>
      );
    }

    const strValue = typeof value === "string" ? value : JSON.stringify(value);
    const isLongText = strValue.length > 80 || strValue.includes("\n");

    return (
      <div key={key} className="flex flex-col gap-1.5 flex-1 min-w-0">
        <label className="text-xs font-bold text-gray-500 uppercase tracking-wider ml-1">
          {label}
        </label>
        {isLongText ? (
          <textarea
            className="w-full min-h-[100px] text-sm text-gray-900 border border-gray-200 rounded-xl px-4 py-3
              focus:outline-none focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500/50 resize-y shadow-sm transition-all leading-relaxed"
            value={strValue}
            onChange={(e) => handleFieldChange(fullPath, e.target.value)}
            placeholder={`Type ${label.toLowerCase()} details here...`}
          />
        ) : (
          <input
            type="text"
            className="w-full text-sm text-gray-900 border border-gray-200 rounded-xl px-4 py-2.5
              focus:outline-none focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500/50 shadow-sm transition-all"
            value={strValue}
            onChange={(e) => handleFieldChange(fullPath, e.target.value)}
            placeholder={`Enter ${label.toLowerCase()}...`}
          />
        )}
      </div>
    );
  };

  const currentSectionData = sections[activeTab];
  const currentIsArray = Array.isArray(currentSectionData);
  const currentIsObject =
    typeof currentSectionData === "object" &&
    currentSectionData !== null &&
    !currentIsArray;

  const renderEditorBody = () => {
    // Template mode: a section is a string, string[], or a nested object
    if (isTemplate) {
      if (currentIsArray) {
        return renderArrayItems(
          currentSectionData as string[],
          formatLabel(activeTab),
          handleTemplateArray,
        );
      }
      if (currentIsObject) {
        // Nested object section — reuse the recursive field renderer
        return Object.entries(currentSectionData).map(([key, value]) =>
          renderField(key, value, []),
        );
      }
      return (
        <div className="flex flex-col gap-2">
          <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">
            {formatLabel(activeTab)}
          </label>
          <textarea
            className="w-full h-80 text-sm text-gray-900 border border-gray-200 rounded-[1.5rem] p-5
              focus:outline-none focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500/50 resize-none leading-relaxed shadow-sm transition-all"
            value={
              typeof currentSectionData === "string"
                ? currentSectionData
                : String(currentSectionData ?? "")
            }
            onChange={(e) => handleTemplateString(e.target.value)}
            placeholder="Enter detailed notes here..."
          />
        </div>
      );
    }

    // SOAP mode (unchanged behavior)
    if (currentIsObject) {
      return Object.entries(currentSectionData).map(([key, value]) =>
        renderField(key, value, []),
      );
    }
    return (
      <div className="flex flex-col gap-2">
        <label className="text-xs font-bold text-gray-400 uppercase tracking-widest ml-1">
          Notes
        </label>
        <textarea
          className="w-full h-80 text-sm text-gray-900 border border-gray-200 rounded-[1.5rem] p-5
            focus:outline-none focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500/50 resize-none leading-relaxed shadow-sm transition-all"
          value={
            typeof currentSectionData === "string"
              ? currentSectionData
              : JSON.stringify(currentSectionData, null, 2)
          }
          onChange={(e) =>
            setSections((prev) => ({
              ...prev,
              [activeTab]: e.target.value,
            }))
          }
          placeholder="Enter detailed notes here..."
        />
      </div>
    );
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/60 backdrop-blur-md p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-3xl flex flex-col max-h-[90vh] overflow-hidden border border-white/20">
        {/* Header */}
        <div className="flex items-center justify-between px-8 py-6 border-b border-gray-100 bg-white/50 backdrop-blur-xl">
          <div className="flex flex-col">
            <h2 className="text-2xl font-black text-gray-900">
              {isTemplate
                ? `Edit ${encounter.template_name || "Document"}`
                : "Edit SOAP Note"}
            </h2>
            <p className="text-xs text-gray-400 font-medium mt-1">
              Refine and update patient encounter details
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2.5 text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded-2xl transition-all"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2.5"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Section Tabs */}
        <div className="flex border-b border-gray-100 px-8 gap-2 pb-0 pt-2 bg-gray-50/50 overflow-x-auto">
          {tabKeys.map((key, index) => {
            const { label, color } = tabMeta(key, index);
            const isActive = activeTab === key;
            return (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`px-5 py-3 text-sm font-bold rounded-t-xl border-b-2 transition-all whitespace-nowrap relative -mb-[2px] ${
                  isActive
                    ? `${TAB_ACTIVE_CLASSES[color] || TAB_ACTIVE_CLASSES.blue} bg-white shadow-[0_-4px_12px_-4px_rgba(0,0,0,0.05)]`
                    : "border-transparent text-gray-400 hover:text-gray-600 hover:bg-gray-100/50"
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>

        {/* Editor Area */}
        <div className="flex-1 overflow-y-auto px-8 py-8 bg-white">
          <div className="flex flex-col gap-8 max-w-2xl mx-auto">
            {renderEditorBody()}
          </div>
        </div>

        {/* Error Feedback */}
        {error && (
          <div className="mx-8 mb-4 px-5 py-4 bg-red-50 border border-red-100 rounded-2xl text-sm text-red-600 flex items-center gap-3 animate-in fade-in slide-in-from-bottom-2">
            <div className="bg-red-100 p-1.5 rounded-full">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <span className="font-medium">{error}</span>
          </div>
        )}

        {/* Footer Actions */}
        <div className="flex items-center justify-end gap-3 px-8 py-6 border-t border-gray-100 bg-gray-50/80 backdrop-blur-xl">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-6 py-2.5 text-sm font-bold text-gray-500 hover:text-gray-900 hover:bg-gray-200/50 rounded-xl transition-all"
          >
            Discard
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-8 py-2.5 text-sm font-bold text-white bg-gray-900 hover:bg-black
              rounded-xl shadow-lg shadow-gray-200 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2.5 active:scale-[0.98]"
          >
            {saving ? (
              <>
                <svg
                  className="w-4 h-4 animate-spin"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v8z"
                  />
                </svg>
                Processing...
              </>
            ) : (
              "Apply Changes"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SOAPEditModal;
